import {Component, inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogContent, MatDialogRef} from '@angular/material/dialog';
import {
  OrderOfferProductsDetailsDialogComponent
} from './order-offer-products-details-dialog/order-offer-products-details-dialog.component';
import {ProductService} from '../../../services/product.service';
import {THEME_CONFIG} from '../../../../environments/tokens/theme.token';
import {CdkScrollable} from '@angular/cdk/scrolling';
import {MatFormField} from '@angular/material/input';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatButton} from '@angular/material/button';
import {DecimalPipe, NgOptimizedImage} from '@angular/common';

interface OfferedProduct {
  id: string;
  name: string;
  image: string;
  price: any;
  available: boolean;
}

interface OfferedCategory {
  quantity: number;
  category: { id: string; name: string };
  size: { id: string; name: string } | null;
  products: OfferedProduct[];
  selectedProductIds: string[];
  selectedQuantities: { [productId: string]: number };
}

@Component({
  selector: 'app-order-offer-products-dialog',
  templateUrl: './order-offer-products-dialog.component.html',
  styleUrl: './order-offer-products-dialog.component.scss',
  standalone: true,
  imports: [CdkScrollable, MatDialogContent,
    MatFormField, MatSelect, MatOption, MatButton, DecimalPipe, NgOptimizedImage]
})
export class OrderOfferProductsDialogComponent implements OnInit {
  readonly data = inject<{ product: any; rule: any; categoryIndex: number }>(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<OrderOfferProductsDialogComponent>);
  private dialog = inject(MatDialog);
  private productService = inject(ProductService);
  selectedSizeId: string | null = null;
  private productCache = new Map<string, any>();

  offeredCategories: OfferedCategory[] = [];
  protected readonly themeConfig = inject(THEME_CONFIG)

  productConfigurations: Map<string, any> = new Map();

  ngOnInit(): void {
    this.offeredCategories = (this.data.rule.categories || [])
      .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .map((cat: any) => ({
        quantity: cat.quantity,
        category: cat.category,
        size: cat.size || null,
        products: (cat.products || []).filter((p: any) => p.available !== false)
          .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0)),
        selectedProductIds: [],
        selectedQuantities: {}
      }));
  }

  getProductQuantity(categoryIndex: number, productId: string): number {
    return this.offeredCategories[categoryIndex].selectedQuantities[productId] || 0;
  }

  getTotalSelectedInCategory(categoryIndex: number): number {
    const cat = this.offeredCategories[categoryIndex];
    return Object.values(cat.selectedQuantities).reduce((sum, qty) => sum + qty, 0);
  }

  incrementProduct(categoryIndex: number, product: any): void {
    const cat = this.offeredCategories[categoryIndex];
    const total = this.getTotalSelectedInCategory(categoryIndex);
    if (total >= cat.quantity) return;

    // ✅ Si déjà en cache → pas d'appel API
    if (this.productCache.has(product.id)) {
      this.handleIncrementLogic(categoryIndex, product, this.productCache.get(product.id));
      return;
    }

    this.productService.getProductById(product.id).subscribe({
      next: (response: any) => {
        const fullProduct = response?.body?.data || product;
        // ✅ Stocker dans le cache
        this.productCache.set(product.id, fullProduct);
        this.handleIncrementLogic(categoryIndex, product, fullProduct);
      },
      error: () => {
        cat.selectedQuantities[product.id] = (cat.selectedQuantities[product.id] || 0) + 1;
        cat.selectedProductIds = this.buildSelectedProductIds(cat);
      }
    });
  }
  /** Catégories pour lesquelles il reste des produits à choisir */
  getRemainingChoices(): { index: number; label: string; remaining: number }[] {
    return this.offeredCategories
      .map((cat, index) => ({
        index,
        label: cat.category.name + (cat.size ? ` (${cat.size.name})` : ''),
        remaining: cat.quantity - this.getTotalSelectedInCategory(index)
      }))
      .filter(r => r.remaining > 0);
  }

  /** Clic sur le bandeau : on remonte à la catégorie concernée */
  scrollToCategory(index: number): void {
    document.getElementById('category-' + index)
      ?.scrollIntoView({behavior: 'smooth', block: 'start'});
  }
  /** Seulement la première catégorie qui n'est pas encore complète */
  getNextRemainingChoice(): { index: number; label: string; remaining: number } | null {
    return this.getRemainingChoices()[0] ?? null;
  }
// ✅ Logique extraite pour éviter la duplication
  private handleIncrementLogic(categoryIndex: number, product: any, fullProduct: any): void {
    const cat = this.offeredCategories[categoryIndex];
    const needsCustomization = fullProduct.ingredients?.length > 0
      || fullProduct.supplements?.length > 0
      || fullProduct.additions?.length > 0;

    if (!needsCustomization) {
      cat.selectedQuantities[product.id] = (cat.selectedQuantities[product.id] || 0) + 1;
      cat.selectedProductIds = this.buildSelectedProductIds(cat);
      if (this.getTotalSelectedInCategory(categoryIndex) === cat.quantity) {
        this.scrollToNextCategory(categoryIndex);
      }
      return;
    }

    const detailDialogRef = this.dialog.open(OrderOfferProductsDetailsDialogComponent, {
      panelClass: 'custom-dialog',
      width: '500px',
      disableClose: true,
      data: {
        product: fullProduct,
        rule: {
          ...this.data.rule,
          selectedSizeId: cat.size?.id || null,
          selectedSizeName: cat.size?.name || null
        },
        categoryIndex
      }
    });

    detailDialogRef.afterClosed().subscribe((result: any) => {
      if (!result || result.action === 'cancel') return;
      if (result.action === 'confirm') {
        const newQty = (cat.selectedQuantities[product.id] || 0) + 1;
        cat.selectedQuantities[product.id] = newQty;
        cat.selectedProductIds = this.buildSelectedProductIds(cat);
        this.productConfigurations.set(
          `${categoryIndex}_${product.id}_${newQty - 1}`,
          result.configuration
        );
        if (this.getTotalSelectedInCategory(categoryIndex) === cat.quantity) {
          this.scrollToNextCategory(categoryIndex);
        }
      }
    });
  }

  getAllSelectedSupplements(): any[] {
    const allSupps: any[] = [];
    this.productConfigurations.forEach((config, key) => {
      if (!config) return;
      const supps = config.selectedSupplements || [];
      supps.forEach((sup: any) => {
        allSupps.push({
          name: sup.name,
          price: sup.price || 0,
          quantity: sup.quantity || 1
        });
      });
    });
    return allSupps;
  }

  getTotalSupplementsPrice(): number {
    return this.getAllSelectedSupplements().reduce((sum, sup) => {
      return sum + (sup.price || 0) * (sup.quantity || 1);
    }, 0);
  }

  decrementProduct(categoryIndex: number, productId: string): void {
    const cat = this.offeredCategories[categoryIndex];
    const currentQty = cat.selectedQuantities[productId] || 0;
    if (currentQty <= 0) return;
    this.productConfigurations.delete(`${categoryIndex}_${productId}_${currentQty - 1}`);

    if (currentQty === 1) {
      delete cat.selectedQuantities[productId];
      this.productConfigurations.delete(`${categoryIndex}_${productId}`);
    } else {
      cat.selectedQuantities[productId] = currentQty - 1;
    }
    cat.selectedProductIds = this.buildSelectedProductIds(cat);
  }

  getDiscountAmount(): number {
    const basePrice = this.getTotalBasePrice();
    const reduction = this.data.rule?.reduction;
    if (!reduction) return 0;

    if (reduction.type === 'PERCENTAGE') {
      return Math.round(basePrice * (reduction.value || 0) * 100) / 100;
    }
    if (reduction.type === 'FIXED_AMOUNT') {
      return Math.min(basePrice, reduction.value || 0); // can't exceed base price
    }
    return 0;
  }

  private buildSelectedProductIds(cat: OfferedCategory): string[] {
    const ids: string[] = [];
    Object.entries(cat.selectedQuantities).forEach(([productId, qty]) => {
      for (let i = 0; i < qty; i++) ids.push(productId);
    });
    return ids;
  }

  isFree(): boolean {
    const r = this.data.rule?.reduction;
    // ✅ Pour FIXED_AMOUNT ce n'est pas "gratuit" par produit
    if (r?.type === 'FIXED_AMOUNT') return false;
    return r?.type === 'PERCENTAGE' && r?.value === 1;
  }

  isFixedAmountReduction(): boolean {
    return this.data.rule?.reduction?.type === 'FIXED_AMOUNT';
  }

  isSetPriceReduction() {
    return this.data.rule?.reduction?.type === 'SET_PRICE';

  }

  getFixedAmountValue(): number {
    return this.data.rule?.reduction?.value || 0;
  }

  getTotalBasePrice(): number {
    return this.offeredCategories.reduce((total, cat) => {
      return total + cat.selectedProductIds.reduce((sum, productId) => {
        const product = cat.products.find((p: any) => p.id === productId);
        // ← FIX: utilise getBasePrice() au lieu de product?.price?.amount
        return sum + this.getBasePrice(product);
      }, 0);
    }, 0);
  }

  // getTotalAfterDiscount(): number {
  //   const total = this.getTotalBasePrice();
  //   const discount = this.getFixedAmountValue();
  //   return Math.max(0, Math.floor((total - discount) * 100) / 100);
  // }
  // getTotalAfterDiscount(): number {
  //   const total = this.getTotalBasePrice();
  //   const discount = this.getFixedAmountValue();
  //   const supplements = this.getTotalSupplementsPrice();
  //   return Math.max(0, Math.floor((total - discount) * 100) / 100) + supplements;
  // }
  getTotalAfterDiscount(): number {
    const basePrice = this.getTotalBasePrice();           // prix des produits seulement
    const supplements = this.getTotalSupplementsPrice();       // suppléments plein tarif

    const reduction = this.data.rule?.reduction;

    let discountedBase = basePrice;
    if (reduction?.type === 'PERCENTAGE') {
      discountedBase = Math.max(0, basePrice * (1 - (reduction.value || 0)));
    } else if (reduction?.type === 'FIXED_AMOUNT') {
      discountedBase = Math.max(0, basePrice - (reduction.value || 0));
      console.log('discountedBase', discountedBase)
    } else if (reduction?.type === 'SET_PRICE') {
      discountedBase = reduction.value;
    }

    // Total final = base après remise + suppléments (non remisés)
    return Math.round((discountedBase + supplements) * 100) / 100;
  }

  getDiscountBadge(): string | null {
    const r = this.data.rule?.reduction;
    if (!r) return null;

    if (r.type === 'PERCENTAGE') {
      if (r.value === 1) return null;
      const pct = Math.round(r.value * 100);
      return `-${pct}%`;
    }

    // ✅ FIXED_AMOUNT → pas de badge par produit (réduction sur le total)
    if (r.type === 'FIXED_AMOUNT') {
      return null;
    }

    return null;
  }

  /** Prix original */
  getBasePrice(product: any): number {
    const price = product?.price;

    // ✅ AJOUTER CE CAS — prix direct en nombre
    if (typeof price === 'number') return price;

    // Si c'est un string comme "2,00 €"
    if (typeof price === 'string') {
      return parseFloat(price.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
    }

    // Si c'est un objet (pizza avec tailles)
    if (typeof price === 'object' && price !== null) {
      const cat = this.offeredCategories.find(c =>
        c.products.some((p: any) => p.id === product.id)
      );
      const sizeName = cat?.size?.name;
      if (sizeName && price[sizeName]) {
        return parseFloat(price[sizeName].replace(/[^\d,]/g, '').replace(',', '.')) || 0;
      }
      if (this.selectedSizeId) {
        const sizeNameFromSelected = this.getProductSizes()
          .find(s => s.id === this.selectedSizeId)?.name;
        if (sizeNameFromSelected && price[sizeNameFromSelected]) {
          return parseFloat(price[sizeNameFromSelected].replace(/[^\d,]/g, '').replace(',', '.')) || 0;
        }
      }
    }

    return 0;
  }

  // getBasePrice(product: any): number {
  //   const price = product?.price;
  //
  //   // Si c'est un string comme "2,00 €"
  //   if (typeof price === 'string') {
  //     return parseFloat(price.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
  //   }
  //
  //   // Si c'est un objet (pizza avec tailles) → utilise la taille du cat ou selectedSizeId
  //   if (typeof price === 'object' && price !== null) {
  //     // Cherche la taille définie dans la catégorie (cat.size)
  //     const cat = this.offeredCategories.find(c =>
  //       c.products.some((p: any) => p.id === product.id)
  //     );
  //     const sizeName = cat?.size?.name;
  //     if (sizeName && price[sizeName]) {
  //       return parseFloat(price[sizeName].replace(/[^\d,]/g, '').replace(',', '.')) || 0;
  //     }
  //     // Fallback: selectedSizeId
  //     if (this.selectedSizeId) {
  //       const sizeNameFromSelected = this.getProductSizes()
  //         .find(s => s.id === this.selectedSizeId)?.name;
  //       if (sizeNameFromSelected && price[sizeNameFromSelected]) {
  //         return parseFloat(price[sizeNameFromSelected].replace(/[^\d,]/g, '').replace(',', '.')) || 0;
  //       }
  //     }
  //   }
  //
  //   return 0;
  // }


  /** Prix après réduction */
  getFinalPrice(product: any): number {
    const basePrice = this.getBasePrice(product);
    if (basePrice <= 0) return 0;

    const reduction = this.data.rule?.reduction;
    if (!reduction) return basePrice;

    if (reduction.type === 'PERCENTAGE') {
      return Math.round(basePrice * (1 - (reduction.value || 0)) * 100) / 100;
    }

    if (reduction.type === 'FIXED_AMOUNT') {
      return Math.max(0, Math.round((basePrice - (reduction.value || 0)) * 100) / 100);
    }
    if (reduction.type === 'SET_PRICE') {
      return reduction.value || 0;
    }

    if (reduction.type === 'SET_PRICE') {
      return reduction.value || 0;
    }

    return basePrice;

  }

  // getFinalPrice(product: any): number {
  //   const base = this.getBasePrice(product);
  //   const r = this.data.rule?.reduction;
  //   if (!r) return base;
  //
  //   if (r.type === 'FIXED_AMOUNT') return base;
  //
  //   if (r.type === 'PERCENTAGE') {
  //     return Math.floor(base * (1 - r.value) * 100) / 100;
  //   }
  //
  //   return base;
  // }
  selectProduct(categoryIndex: number, product: any): void {
    if (!product.available) return;

    const cat = this.offeredCategories[categoryIndex];
    const alreadySelected = cat.selectedProductIds.includes(product.id);

    if (alreadySelected) {
      cat.selectedProductIds = cat.selectedProductIds.filter(id => id !== product.id);
      this.productConfigurations.delete(`${categoryIndex}_${product.id}`);
      return;
    }

    if (cat.selectedProductIds.length >= cat.quantity) return;

    // ✅ Cache check
    if (this.productCache.has(product.id)) {
      this.handleSelectLogic(categoryIndex, product, this.productCache.get(product.id));
      return;
    }

    this.productService.getProductById(product.id).subscribe({
      next: (response: any) => {
        const fullProduct = response?.body?.data || product;
        this.productCache.set(product.id, fullProduct);
        this.handleSelectLogic(categoryIndex, product, fullProduct);
      },
      error: () => {
        cat.selectedProductIds.push(product.id);
        this.productConfigurations.set(`${categoryIndex}_${product.id}`, null);
      }
    });
  }

  private handleSelectLogic(categoryIndex: number, product: any, fullProduct: any): void {
    const cat = this.offeredCategories[categoryIndex];
    const needsCustomization = fullProduct.ingredients?.length > 0
      || fullProduct.supplements?.length > 0
      || fullProduct.additions?.length > 0;

    if (!needsCustomization) {
      cat.selectedProductIds.push(product.id);
      this.productConfigurations.set(`${categoryIndex}_${product.id}`, null);
      return;
    }

    const detailDialogRef = this.dialog.open(OrderOfferProductsDetailsDialogComponent, {
      panelClass: 'custom-dialog',
      width: '500px',
      disableClose: true,
      data: {
        product: fullProduct,
        rule: {
          ...this.data.rule,
          selectedSizeId: cat.size?.id || null,
          selectedSizeName: cat.size?.name || null
        },
        categoryIndex
      }
    });

    detailDialogRef.afterClosed().subscribe((result: any) => {
      if (!result || result.action === 'cancel') return;
      if (result.action === 'confirm') {
        cat.selectedProductIds.push(result.product.id);
        this.productConfigurations.set(`${categoryIndex}_${result.product.id}`, result.configuration);
      }
    });
  }

  // allSelected(): boolean {
  //   return this.offeredCategories.every(cat => cat.selectedProductIds.length === cat.quantity);
  // }
  allSelected(): boolean {
    return this.offeredCategories.every(
      (cat, index) => this.getTotalSelectedInCategory(index) === cat.quantity
    );
  }

  hasSelection(): boolean {
    return this.offeredCategories.some((cat, index) =>
      this.getTotalSelectedInCategory(index) > 0
    );
  }

  getSelectedCount(): number {
    return this.offeredCategories.filter(cat => cat.selectedProductIds.length === cat.quantity).length;
  }

  cancel(): void {
    this.dialogRef.close({action: 'cancel'});
  }

  // confirm(): void {
  //   const selectedProducts = this.offeredCategories.flatMap((cat, index) =>
  //     cat.selectedProductIds.map(productId => {
  //       const product = cat.products.find(p => p.id === productId);
  //       const configuration = this.productConfigurations.get(`${index}_${productId}`) || null;
  //       return { category: cat.category, size: cat.size, quantity: 1, product, configuration };
  //     })
  //   );
  //   this.dialogRef.close({ action: 'confirm', selectedProducts });
  // }
  productHasSizes(): boolean {
    return typeof this.data.product?.price === 'object' && this.data.product?.price !== null;
  }

  // ← AJOUTE
  getProductSizes(): { id: string; name: string }[] {
    const price = this.data.product?.price;
    if (typeof price === 'object' && price !== null) {
      return Object.keys(price).map((name, i) => ({id: `size_${i}`, name}));
    }
    return [];
  }

  // ← AJOUTE
  getProductPrice(): number {
    const price = this.data.product?.price;
    if (typeof price === 'string') {
      return parseFloat(price.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
    }
    if (typeof price === 'object' && this.selectedSizeId) {
      const sizeName = this.getProductSizes().find(s => s.id === this.selectedSizeId)?.name;
      if (sizeName && price[sizeName]) {
        return parseFloat(price[sizeName].replace(/[^\d,]/g, '').replace(',', '.')) || 0;
      }
    }
    return 0;
  }

  // ← AJOUTE
  onSizeChange(sizeId: string | null): void {
    this.selectedSizeId = sizeId || null;
  }

  confirm(): void {
    const selectedProducts = this.offeredCategories.flatMap((cat, index) => {
      const result: any[] = [];
      Object.entries(cat.selectedQuantities).forEach(([productId, qty]) => {
        const product = cat.products.find(p => p.id === productId);
        for (let i = 0; i < qty; i++) {
          // Cherche config par instance, sinon config générale
          const configuration =
            this.productConfigurations.get(`${index}_${productId}_${i}`) ||
            this.productConfigurations.get(`${index}_${productId}`) ||
            null;
          result.push({
            category: cat.category,
            size: cat.size,
            quantity: 1, // ← chaque instance = quantité 1
            product,
            configuration,
            // size: this.selectedSizeId
            //   ? this.getProductSizes().find(s => s.id === this.selectedSizeId) || null
            //   : null
          });
        }
      });
      return result;
    });
    this.dialogRef.close({action: 'confirm', selectedProducts});
  }

  private scrollToNextCategory(currentIndex: number): void {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= this.offeredCategories.length) return;

    setTimeout(() => {
      const el = document.getElementById('category-' + nextIndex);
      el?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, 150); // petit délai pour que l'UI se mette à jour d'abord
  }

  trackCompositionLine(line: string, index: number) {
    return `${line}=${index}`
  }
}
